/**
 * Date formatting utilities for human-readable dates
 */

/**
 * Format date to human-readable string with timezone
 */
export function formatDate(date: Date | string, timezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (!timezone) {
    return d.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  });
}

/**
 * Format date and time to human-readable string with timezone
 */
export function formatDateTime(date: Date | string, timezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (!timezone) {
    return d.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
}

/**
 * Format time to human-readable string with timezone
 */
export function formatTime(date: Date | string, timezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (!timezone) {
    return d.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  
  return d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
}

/**
 * Get relative time string (e.g., "через 2 часа", "через 3 дня")
 */
export function formatRelativeTime(date: Date | string, timezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMs < 0) {
    // Past date
    if (Math.abs(diffDays) > 0) {
      return `${Math.abs(diffDays)} ${getDayWord(Math.abs(diffDays))} назад`;
    }
    if (Math.abs(diffHours) > 0) {
      return `${Math.abs(diffHours)} ${getHourWord(Math.abs(diffHours))} назад`;
    }
    return `${Math.abs(diffMinutes)} ${getMinuteWord(Math.abs(diffMinutes))} назад`;
  }
  
  // Future date
  if (diffDays > 0) {
    return `через ${diffDays} ${getDayWord(diffDays)}`;
  }
  if (diffHours > 0) {
    return `через ${diffHours} ${getHourWord(diffHours)}`;
  }
  if (diffMinutes > 0) {
    return `через ${diffMinutes} ${getMinuteWord(diffMinutes)}`;
  }
  return 'скоро';
}

/**
 * Get day word in correct form
 */
function getDayWord(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) {
    return 'день';
  }
  if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) {
    return 'дня';
  }
  return 'дней';
}

/**
 * Get hour word in correct form
 */
function getHourWord(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) {
    return 'час';
  }
  if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) {
    return 'часа';
  }
  return 'часов';
}

/**
 * Get minute word in correct form
 */
function getMinuteWord(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) {
    return 'минуту';
  }
  if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) {
    return 'минуты';
  }
  return 'минут';
}

/**
 * Group items by date
 */
export function groupByDate<T extends { due?: string; scheduledAt?: string }>(
  items: T[],
  timezone?: string
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  
  for (const item of items) {
    const dateStr = item.due || item.scheduledAt;
    if (!dateStr) {
      const key = 'Без даты';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
      continue;
    }
    
    const date = new Date(dateStr);
    const key = formatDate(date, timezone);
    
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(item);
  }
  
  return groups;
}

/**
 * Get date key for grouping (today, tomorrow, date)
 */
export function getDateGroupKey(date: Date | string, timezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  
  // Normalize dates to start of day for comparison
  const dateStart = new Date(d);
  dateStart.setHours(0, 0, 0, 0);
  
  const nowStart = new Date(now);
  nowStart.setHours(0, 0, 0, 0);
  
  const diffDays = Math.floor((dateStart.getTime() - nowStart.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Сегодня';
  }
  if (diffDays === 1) {
    return 'Завтра';
  }
  if (diffDays === -1) {
    return 'Вчера';
  }
  if (diffDays > 1 && diffDays <= 7) {
    return d.toLocaleDateString('ru-RU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: timezone,
    });
  }
  
  return formatDate(d, timezone);
}

